// Reactive free-scan quota state, shared between the request layer and the UI.
// secureFetch updates it from every session bootstrap and, critically, flips
// `blocked` when the API returns 402 QUOTA_EXCEEDED so the paywall can show.

import { reactive } from 'vue';

export interface QuotaInfo {
  used: number;
  limit: number;
  paid: boolean;
  device: string;
}

/** Which quota tripped: the per-device limit or the per-network (IP) limit. */
export type QuotaScope = 'device' | 'network';

export interface QuotaState extends QuotaInfo {
  blocked: boolean;
  payContact: string;
  scope: QuotaScope;
}

export const quotaState = reactive<QuotaState>({
  used: 0,
  limit: 0,
  paid: false,
  device: '',
  blocked: false,
  payContact: '@autospy_group',
  scope: 'device',
});

type QuotaPatch = {
  used?: number | undefined;
  limit?: number | undefined;
  paid?: boolean | undefined;
  device?: string | undefined;
};

/** Merge the latest quota snapshot (from /session/init) without unblocking. */
export function setQuota(info: QuotaPatch): void {
  if (typeof info.used === 'number') quotaState.used = info.used;
  if (typeof info.limit === 'number') quotaState.limit = info.limit;
  if (typeof info.paid === 'boolean') quotaState.paid = info.paid;
  if (typeof info.device === 'string') quotaState.device = info.device;
}

/** Mark the device/network as over quota; the app should show the paywall. */
export function markBlocked(payload: {
  used?: number | undefined;
  limit?: number | undefined;
  device?: string | undefined;
  payContact?: string | undefined;
  scope?: QuotaScope | undefined;
}): void {
  setQuota(payload);
  if (payload.payContact) quotaState.payContact = payload.payContact;
  quotaState.scope = payload.scope === 'network' ? 'network' : 'device';
  quotaState.blocked = true;
}
